import { useState } from 'react';
import {
  Button,
  Checkbox,
  HeadingText,
  ParagraphText,
  StackLayout,
  Box,
  Icon,
  Modal,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { FormProvider, useForm } from '@leagueplatform/web-common';
import { captureAppError } from '@leagueplatform/observability';
import { useIntl } from '@leagueplatform/locales';
import { useSelector } from '@xstate/react';
import { OnboardingCard } from 'components/onboarding/onboarding-card/onboarding-card.component';
import { useIsOnboardingMobile } from 'hooks/use-is-onboarding-mobile/use-is-onboarding-mobile.hook';
import { RHFTextInput } from 'components/form-elements/rhf-text-input.component';
import {
  emailValidation,
  phoneNumberValidation,
} from 'common/utils/rhf-form-validation.util';
import {
  useUpdateAccountSetup,
  type AccountSetupInputs,
} from 'hooks/use-update-account-setup/use-update-account-setup.hook';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { SENTRY_CONSTANTS } from 'common/constants';
import accountSetupIllustration from 'assets/account-setup-illustration.svg';

type AccountSetupFormValues = {
  preferredFirstName: string;
  mobilePhoneNumber: string;
  preferredPhoneNumber: string;
  email: string;
  goPaperless: boolean;
};

export const AccountSetupView = () => {
  const { formatMessage } = useIntl();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isOnboardingMobile = useIsOnboardingMobile();
  const [valuesAfterSaveFailure, setValuesAfterSaveFailure] =
    useState<AccountSetupFormValues | null>(null);

  const actor = OnboardingMachineContext.useActorRef();
  const { send } = actor;
  const preferredFirstName = useSelector(
    actor,
    (snapshot) => snapshot.context.preferredFirstName,
  );

  // useForm reads defaultValues once at mount and never reconciles them
  // with later prop changes. This is safe here because the parent
  // OnboardingGate doesn't render <AccountSetupView /> until the machine
  // exits `initializing` — meaning the snapshot context fields are
  // populated by the time this hook runs.
  const methods = useForm<AccountSetupFormValues>({
    // Per Figma 6326-292804: validation fires on blur, then re-validates on
    // each subsequent keystroke (RHF's reValidateMode default) so errors
    // clear as soon as the value passes — and only when it passes.
    mode: 'onBlur',
    defaultValues: {
      preferredFirstName,
      mobilePhoneNumber: '',
      preferredPhoneNumber: '',
      email: '',
      // Go Paperless is an explicit opt-in, so it always starts unchecked
      // rather than reflecting any existing Comm Prefs value.
      goPaperless: false,
    },
  });

  // dirtyFields must be read during render: RHF's formState is a Proxy that
  // only subscribes to the properties accessed in the render phase — a lazy
  // read inside the submit callback can return a stale/empty object.
  const { isDirty, isSubmitting, dirtyFields } = methods.formState;
  // Drives the email field's aria-required, so the conditional requirement is
  // announced to assistive tech and not merely enforced. The validation rule
  // itself reads the value through getValues rather than this — see below.
  const isGoPaperlessChecked = methods.watch('goPaperless');
  const { updateAccountSetup } = useUpdateAccountSetup();

  const navigateToNextView = (values?: Partial<AccountSetupFormValues>) =>
    send({ type: 'navigateToNextView', payload: { ...values } });

  const handleFormSubmit = async (values: AccountSetupFormValues) => {
    setSubmitError(null);
    if (!isDirty) {
      // Pristine submit (form never edited) still seeds the machine context
      // with the form values, so a subsequent step that reads phones / email
      // from `OnboardingMachineContext` sees a populated payload.
      navigateToNextView(values);
      return;
    }

    // Send only the fields the member actually edited. Per the Comm Prefs
    // §6.5 contract, omitted = "don't modify" but explicit null = "clear it"
    // — forwarding untouched fields would actively rewrite values in GHP's
    // system on every submit.
    const editedValues: AccountSetupInputs = {};
    if (dirtyFields.preferredFirstName) {
      editedValues.preferredFirstName = values.preferredFirstName;
    }
    if (dirtyFields.mobilePhoneNumber) {
      editedValues.mobilePhoneNumber = values.mobilePhoneNumber;
    }
    if (dirtyFields.preferredPhoneNumber) {
      editedValues.preferredPhoneNumber = values.preferredPhoneNumber;
    }
    if (dirtyFields.email) {
      editedValues.email = values.email;
    }
    // Only ever an opt-in: unchecked is this screen's default, so leaving the box
    // alone must not PATCH `false` over a preference set elsewhere.
    if (values.goPaperless) {
      editedValues.goPaperless = true;
    }

    try {
      const { saveFailed } = await updateAccountSetup(editedValues);
      if (saveFailed) {
        setValuesAfterSaveFailure(values);
        return;
      }
      navigateToNextView(values);
    } catch (error) {
      if (error instanceof Error) {
        captureAppError(error, {
          ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
          errorName: 'Error saving Account Setup form',
          context: {
            Context: {
              description: formatMessage({ id: 'STR_ACCOUNT_SETUP_HEADING' }),
            },
          },
        });
      }
      setSubmitError(
        formatMessage({ id: 'STR_SOMETHING_WENT_WRONG_TRY_AGAIN' }),
      );
    }
  };

  const continueAfterSaveFailure = () => {
    if (!valuesAfterSaveFailure) return;
    navigateToNextView(valuesAfterSaveFailure);
    setValuesAfterSaveFailure(null);
  };

  return (
    <OnboardingCard>
      <StackLayout
        spacing="$oneAndHalf"
        horizontalAlignment="center"
        css={{
          paddingBlockStart: '$twoAndHalf',
          paddingInline: '$twoAndHalf',
        }}
      >
        <StackLayout
          spacing="$half"
          horizontalAlignment="center"
          css={{ width: '100%' }}
        >
          <HeadingText
            size="xl"
            level="2"
            as="h1"
            css={{ textAlign: 'center' }}
          >
            {formatMessage({ id: 'STR_ACCOUNT_SETUP_HEADING' })}
          </HeadingText>
          <ParagraphText css={{ textAlign: 'center' }}>
            {formatMessage({ id: 'STR_ACCOUNT_SETUP_SUBHEADING' })}
          </ParagraphText>
        </StackLayout>
        <Box
          as="img"
          src={accountSetupIllustration}
          alt=""
          // HTML width/height attributes reserve intrinsic dimensions so
          // the browser can prevent layout shift while the asset loads.
          width={160}
          height={160}
          css={{ display: 'block' }}
        />
      </StackLayout>

      <FormProvider formMethods={methods} onSubmit={handleFormSubmit}>
        <StackLayout
          spacing="$one"
          horizontalAlignment="stretch"
          css={{
            paddingInline: '$twoAndHalf',
            paddingBlockStart: '$two',
            width: '100%',
          }}
        >
          <RHFTextInput
            id="preferredFirstName"
            type="text"
            autoComplete="nickname"
            label={formatMessage({ id: 'STR_PREFERRED_NAME' })}
          />
          <RHFTextInput
            id="mobilePhoneNumber"
            type="tel"
            // Spec-ideal is the token sequence "mobile tel", but Genesis's
            // GDSHTMLAutocomplete is single-token; browsers treat the
            // contact-type modifier as an optional hint, so plain "tel"
            // autofills identically in practice.
            autoComplete="tel"
            label={formatMessage({ id: 'STR_MOBILE_PHONE_NUMBER' })}
            registerOptions={phoneNumberValidation(
              formatMessage({ id: 'STR_PHONE_VALIDATION_ERROR' }),
            )}
          />
          <RHFTextInput
            id="preferredPhoneNumber"
            type="tel"
            autoComplete="tel"
            label={formatMessage({ id: 'STR_PREFERRED_PHONE_NUMBER' })}
            registerOptions={phoneNumberValidation(
              formatMessage({ id: 'STR_PHONE_VALIDATION_ERROR' }),
            )}
          />
          <RHFTextInput
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            label={formatMessage({ id: 'STR_EMAIL_ADDRESS' })}
            // Announces the conditional requirement to assistive tech. Passed
            // as aria-required rather than RHFTextInput's `required` prop,
            // which it consumes only to build the RHF rule and never forwards
            // to the input — and rather than the native `required` attribute,
            // which would let the browser's constraint validation block submit
            // with its own bubble instead of the styled Figma error.
            aria-required={isGoPaperlessChecked}
            registerOptions={{
              validate: {
                // Order gives the precedence we want for free: RHF reports the
                // first failing rule, `validEmail` passes on an empty value, so
                // empty + checked surfaces "email required" while a malformed
                // value surfaces the format error.
                ...emailValidation(
                  formatMessage({ id: 'STR_EMAIL_VALIDATION_ERROR' }),
                ).validate,
                // Reads the flag through getValues at validation time, not the
                // watched value: the checkbox's onChange triggers this rule
                // synchronously, before the re-render that would update a
                // prop-driven rule — so a watched value would still be stale
                // here and the error would not appear until the next submit.
                paperlessRequiresEmail: (value: string) =>
                  !methods.getValues('goPaperless') ||
                  value.length > 0 ||
                  formatMessage({
                    id: 'STR_ACCOUNT_SETUP_PAPERLESS_EMAIL_REQUIRED',
                  }),
              },
            }}
          />

          <Checkbox
            id="goPaperless"
            label={formatMessage({ id: 'STR_ACCOUNT_SETUP_GO_PAPERLESS' })}
            // Genesis wraps the inline layout in a label padded by $half, which
            // insets the indicator from the flush left edge of the text inputs
            // above. Pull that back so the two line up, keeping the padding
            // itself for the label's hover background and focus ring.
            css={{ marginInlineStart: 'calc(-1 * $half)' }}
            {...methods.register('goPaperless', {
              // Re-run the email rules on toggle so checking with no email
              // raises the error immediately and unchecking clears it, rather
              // than waiting for the next blur or submit.
              onChange: () => methods.trigger('email'),
            })}
          />
        </StackLayout>

        <Box
          css={{
            marginInline: '$twoAndHalf',
            marginBlockStart: '$two',
            padding: '$one',
            background: '$surfaceCardDisabled',
            borderRadius: '$small',
          }}
        >
          <StackLayout spacing="$one">
            <StackLayout
              orientation="horizontal"
              spacing="$half"
              verticalAlignment="center"
            >
              <Icon
                icon="tinyHelperFilled"
                size={16}
                tint="$onSurfaceIconPrimary"
                label=""
              />
              <UtilityText
                size="eyebrow"
                css={{ color: '$onSurfaceTextPrimary' }}
              >
                {formatMessage({
                  id: 'STR_ACCOUNT_SETUP_DISCLAIMER_TITLE',
                })}
              </UtilityText>
            </StackLayout>
            {/* Plain Box, not a spaced StackLayout: ParagraphText already adds
                  marginBlockEnd $one between siblings, so a stack gap on top of it
                  doubled every paragraph gap to 32px. */}
            <Box>
              <ParagraphText size="sm">
                {formatMessage({ id: 'STR_ACCOUNT_SETUP_DISCLAIMER_P1' })}
              </ParagraphText>
              <ParagraphText size="sm">
                {formatMessage({ id: 'STR_ACCOUNT_SETUP_DISCLAIMER_P2' })}
              </ParagraphText>
              <ParagraphText size="sm">
                {formatMessage({ id: 'STR_ACCOUNT_SETUP_DISCLAIMER_P3' })}
              </ParagraphText>
              <ParagraphText size="sm">
                {formatMessage({ id: 'STR_ACCOUNT_SETUP_DISCLAIMER_P4' })}
              </ParagraphText>
            </Box>
          </StackLayout>
        </Box>

        {submitError && (
          <ParagraphText
            role="alert"
            css={{
              color: '$onSurfaceTextCritical',
              paddingInline: '$twoAndHalf',
              paddingBlockStart: '$one',
            }}
          >
            {submitError}
          </ParagraphText>
        )}

        <Box
          css={{
            borderColor: '$onSurfaceBorderDefault',
            borderStyle: 'solid',
            borderBlockStartWidth: 'thin',
            marginBlockStart: '$twoAndHalf',
            padding: '$one',
            paddingInline: '$two',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="submit"
            loading={isSubmitting}
            priority="primary"
            width={isOnboardingMobile ? 'fillContainer' : 'hugContents'}
          >
            {formatMessage({ id: 'STR_CONTINUE' })}
          </Button>
        </Box>
      </FormProvider>

      <Modal.Root
        open={valuesAfterSaveFailure !== null}
        onOpenChange={(open) => {
          if (!open) continueAfterSaveFailure();
        }}
      >
        <Modal.Content
          width={{ '@initial': 480, '@mobile': '100%' }}
          showCloseButton={false}
        >
          <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
            <StackLayout spacing="$half">
              <Modal.Title>
                <HeadingText level="2" size="lg">
                  {formatMessage({ id: 'STR_ERROR_STATE_HEADING_GENERIC' })}
                </HeadingText>
              </Modal.Title>
              <Modal.Description>
                <ParagraphText css={{ color: '$onSurfaceTextSubdued' }}>
                  {formatMessage({ id: 'STR_ACCOUNT_SETUP_SAVE_ERROR_BODY' })}
                </ParagraphText>
              </Modal.Description>
            </StackLayout>
            <Button
              priority="primary"
              onClick={continueAfterSaveFailure}
              width={isOnboardingMobile ? 'fillContainer' : 'hugContents'}
            >
              {formatMessage({ id: 'STR_OK' })}
            </Button>
          </StackLayout>
        </Modal.Content>
      </Modal.Root>
    </OnboardingCard>
  );
};
